/* eslint-disable @typescript-eslint/member-ordering */
import { formatDate } from '@angular/common';
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    FormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { IdentityService } from 'src/app/core/identity.service';
import { ContractMinDaysStatus } from 'src/app/margin-calculator/models/contract-min-days-status.model';
import { MarginCalculatorMarginService } from 'src/app/margin-calculator/services/margin-calculator-margin.service';
import { DirectOfferCreateMarginDialogComponent } from 'src/app/recruiting/direct-offer-margin/direct-offer-create-margin-dialog.component';
import { DirectOfferImportMarginDialogComponent } from 'src/app/recruiting/direct-offer-margin/direct-offer-import-margin-dialog.component';
import { DirectOfferMarginService } from 'src/app/recruiting/direct-offer-margin/direct-offer-margin.service';
import { offerRescindedSubStatuses } from 'src/app/root-store/account-managers-store/account-managers.selectors';
import { getSubmittalStatuses } from 'src/app/root-store/account-managers-store/actions/account-managers.actions';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { CodeBustersFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/code-busters-feature-flags.enum';
import { OfferStatus, OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { ListItem } from 'src/app/shared/models/list-item.model';
import {
    Attachment,
    Candidates,
    DeclineReasons,
    DetailLookups,
    Expertise,
    Facilities,
    Facility,
    FacilityLookups,
    Offer,
    OfferDetails,
    OfferJobDetails,
    OfferNotes,
    OfferRates,
    OfferType,
    ProfessionTypes,
    Professions,
    RTOApprovalStatuses,
    VendorLookups,
    VendorOptions
} from 'src/app/shared/models/offers.model';
import { DialogSharedDeclineOfferComponent } from 'src/app/shared/offers-shared/decline-dialog/decline-dialog.component';
import { DeclinePanelComponent } from '../decline-panel/decline-panel.component';
import { DialogContractRequestedOfferComponent } from 'src/app/shared/offers-shared/offer-details/dialog-contract-requested-offer/dialog-contract-requested-offer.component';
import { OfferMarginDifference } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/model/offer-margin-difference.model';
import { OfferMarginDifferencesDialogComponent } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/offer-margin-differences-dialog.component';
import { OfferReasonDialogComponent } from 'src/app/shared/offers-shared/offer-dialogs/offer-reason-dialog/offer-reason-dialog.component';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { SharedOffersService as OffersHelper } from 'src/app/shared/services/offers.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { EmploymentTypeCode } from '../../models/enums/employment-type-code.enum';
import { OfferReasonResult } from '../../models/offer-reason-result.model';
import { SubmittalSubStatus } from '../../models/submittal-status.model';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { UnitDescriptionSecureWindowService } from '../../services/unit-description-secure-window.service';
import { CandidateOfferNoteCategory } from 'src/app/shared/models/enums/candidate-offer-note-category.enum';
import { DialogRequestedChangesComponent } from './dialog-request-changes/dialog-request-changes.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { RtoDatesHelpers } from '../../helpers';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { TextFieldModule } from '@angular/cdk/text-field';
import { SharedNotesComponent } from '../notes-display/notes-display.component';
import { RtoWarningMessageComponent } from '../../rto-alerts/rto-warning-message/rto-warning-message';
import { DetailToolbarComponent } from '../../detail-toolbar.component';
import { ArrayContainsPipe } from '../../pipes/array-contains.pipe';
import { BannerComponent } from '../../banner.component';
import { RtoAlertIconComponent } from '../../rto-alerts/rto-alert-icon/rto-alert-icon';
import { SharedOfferAttachmentsComponent } from '../offer-attachments/offer-attachments.component';
import { RecruiterBonusComponent } from '../../components/recruiter-bonus/recruiter-bonus.component';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';

@UntilDestroy()
@Component({
    selector: 'nova-shared-offer-details',
    templateUrl: './offer-details.component.html',
    styleUrls: ['./offer-details.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatCheckboxModule,
        MatRadioModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatCardModule,
        MatMenuModule,
        TextFieldModule,
        SharedNotesComponent,
        RtoWarningMessageComponent,
        DetailToolbarComponent,
        ArrayContainsPipe,
        BannerComponent,
        RtoAlertIconComponent,
        SharedOfferAttachmentsComponent,
        RecruiterBonusComponent,
        TimePickerModule
    ]
})
export class SharedOfferDetailsComponent implements OnInit, OnChanges {
    readonly featureFlag = FeatureFlag;
    offerData: Offer;
    _offerDetails: OfferDetails;
    _updatedDetails: OfferJobDetails;
    lookups: DetailLookups;
    _expertise: Expertise[];
    _offerRates: OfferRates;
    _vendorRate: number;
    facilityLookup: FacilityLookups;
    rateEnabled = false;
    accountManagerName: string;
    attachmentData: Attachment[];
    offerId: number;
    marginId: number;
    vendorLookups: VendorLookups;
    initialFacilityLookup = true;
    constDateSelected = 'date-selected';
    constDateContractStartEndClass = 'date-contract-start-or-end';
    title: string;
    offerTypeEnum = OfferType;
    offerStatus = OfferStatus;
    offersUserTypeEnum = OffersUserType;
    offerNotesCategory = CandidateOfferNoteCategory;
    ayaAppsDomain: string;
    offerRescindedSubStauses$: Observable<SubmittalSubStatus[]>;
    isDeclineOfferCategoryEnabled: boolean;
    isRecruiterSpecificNoteFlagEnabled = false;
    isOperationProcessing: boolean = false;
    isLotusOneUrlReference = false;

    isPanelOpen = false;

    @ViewChild('clearCalendarTrigger') clearCalendarTrigger: MatMenuTrigger;
    @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;

    @Input() isLoading: boolean;
    @Input() submittalId: number;

    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() userType: OffersUserType;
    @Input() attachmentsHeadingTitle: string;
    @Input() attachmentsHeadingTitleSubtext: string;
    @Input() declineReasons: DeclineReasons[];
    @Input() vendorOptions: VendorOptions[];
    @Input() facilityOptions: ListItem[];

    @Input() notesHeadingTitle = 'Notes/History';
    @Input() notesHeadingTitleSubtext = '';
    @Input() canModify: boolean;
    @Input() disableShowButtons = false;

    @Input()
    set contractLength(contractLength: number) {
        if (contractLength) {
            this.displayedContractLengthWeeks = contractLength;
            this.detailsFormGroup.get('contractLength').patchValue(contractLength);
        }
    }

    @Input()
    set contractMinDaysStatus(contractMinDaysStatus: ContractMinDaysStatus) {
        if (contractMinDaysStatus) {
            this.detailsFormGroup.get('contractLengthDays').patchValue(contractMinDaysStatus.contractDays);
            this.displayedContractLengthDays = contractMinDaysStatus.contractDays;
            this.contractLengthDaysApprovalRequired = false;

            if (contractMinDaysStatus.approvalRequired) {
                this.contractLengthDaysInfoMessage = `Contract does not meet the ${contractMinDaysStatus.contractMinDays}
                day minimum for the state of ${contractMinDaysStatus.stateAbbreviation}.
                The Base Bill Rate for the contract length will need approval after submittal.`;
                this.contractLengthDaysApprovalRequired = true;
            }
        }
    }

    @Input()
    set offer(offer: Offer) {
        if (offer) {
            this.offerData = offer;
            this.offerId = offer.id;
            this.marginId = offer.marginId;
            this.title = ` Offer Details - ${this.offerData?.candidateName}`;
            if (offer.offerType === OfferType.Agency) {
                this.setAgencyOfferFieldsAndValidations();
            } else {
                this.setDirectOfferFieldsAndValidattions();
            }
            this.detailsFormGroup?.patchValue({
                professionId: this.offerData.jobProfessionId,
                candidateProfessionId: this.offerData.professionId
            });

            this.jobProfessionName = this.offerData.jobProfessionName;

            this.initContractLengthDaysAndWeeks(offer);
            this.setContractDateFieldsValidations();
        }
    }

    get offer(): Offer {
        return this.offerData;
    }

    @Input()
    set offerDetails(offerDetails: OfferDetails) {
        this._offerDetails = offerDetails;
        if (this._offerDetails) {
            this.patchEditValues();
            if (!offerDetails.facilityId) {
                this.detailsFormGroup.controls.candidateProfessionId.disable();
                this.detailsFormGroup.controls.expertiseId.disable();
            }
        }
    }

    get offerDetails(): OfferDetails {
        return this._offerDetails;
    }

    @Input()
    offerMarginDifferences: OfferMarginDifference[];

    @Input()
    set updatedJobIdDetails(updatedDetails: OfferJobDetails) {
        if (updatedDetails) {
            this._updatedDetails = updatedDetails;
            const timeStart = dayjs().toDate();
            const timeAltStart = dayjs().toDate();
            const timeEnd = dayjs().toDate();
            const timeAltEnd = dayjs().toDate();

            timeStart.setHours(updatedDetails.shiftH);
            timeStart.setMinutes(updatedDetails.shiftM);

            timeEnd.setHours(updatedDetails.shiftToH);
            timeEnd.setMinutes(updatedDetails.shiftToM);

            timeAltStart.setHours(updatedDetails.shiftAltH);
            timeAltStart.setMinutes(updatedDetails.shiftAltM);

            timeAltEnd.setHours(updatedDetails.shiftAltToH);
            timeAltEnd.setMinutes(updatedDetails.shiftAltToM);

            this.professionSelection(updatedDetails.professionId);
            this.minEndDate = new Date(updatedDetails.startDate);

            this.detailsFormGroup.patchValue({
                ...updatedDetails,
                shiftTimeStart: timeStart,
                shiftTimeEnd: timeEnd,
                shiftAltTimeStart: timeAltStart,
                shiftAltTimeEnd: timeAltEnd,
                startDate: new Date(updatedDetails.startDate),
                endDate: new Date(updatedDetails.endDate)
            });
            if (!updatedDetails.facilityId) {
                this.detailsFormGroup.controls.candidateProfessionId.disable();
                this.detailsFormGroup.controls.expertiseId.disable();
            }

            const professionLookup = this.facilityLookups?.professions.find(
                (p) => p.id === updatedDetails.professionId
            );
            if (professionLookup) {
                this.detailsFormGroup.get('professionId').patchValue(professionLookup.id);
                this.jobProfessionName = professionLookup.name;
            } else {
                this.detailsFormGroup.get('professionId').setValue('');
                this.jobProfessionName = null;
            }
        }
    }

    get updatedJobIdDetails(): OfferJobDetails {
        return this._updatedDetails;
    }

    @Input()
    set detailLookups(detailLookups: DetailLookups) {
        if (detailLookups) {
            this.professionTypes = detailLookups.professionTypes;
            this.lookups = detailLookups;
            if (this.offerData?.offerType === OfferType.Direct) {
                this.lookups.contracts = this.lookups.contracts.filter((x) => x.code !== 'COC');
            }
            this.facilities = detailLookups.facilities;
            const id = this.detailsFormGroup.get('facilityId').value;
            const findFacility = this.facilities.find((obj) => obj.id === id);
            this.detailsFormGroup.get('facilityValues').patchValue(findFacility);
            if (!findFacility) {
                this.detailsFormGroup.get('facilityValues').reset();
            }
        }
    }

    get detailLookups(): DetailLookups {
        return this.lookups;
    }

    @Input()
    set facilityLookups(facilityLookups: FacilityLookups) {
        if (facilityLookups) {
            this.facilityLookup = facilityLookups;
            if (this.initialFacilityLookup) {
                this.initialFacilityLookup = false;
            } else {
                this.detailsFormGroup
                    .get('otherDetails')
                    .patchValue(this.offer.otherDetails ?? this.facilityLookup.otherDetails);
            }
            if (facilityLookups.jobs) {
                this.detailsFormGroup.get('jobId').enable();
                this.filteredJobIdOptions = this.detailsFormGroup.get('jobId').valueChanges.pipe(
                    startWith(''),
                    map((val) => this.filterJobIds(val))
                );
            }
            const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
            if (!candidateProfessionId) {
                this.detailsFormGroup.patchValue({
                    expertiseId: null
                });
            }
        }
    }

    get facilityLookups(): FacilityLookups {
        return this.facilityLookup;
    }

    @Input()
    set expertise(expertise: Expertise[]) {
        this._expertise = expertise;
    }

    get expertise(): Expertise[] {
        return this._expertise;
    }

    @Input()
    set updatedRates(rates: OfferRates) {
        if (rates) {
            this._offerRates = rates;
            this.detailsFormGroup.patchValue({ facilityRate: rates.facilityRate });
            this.detailsFormGroup.patchValue({ vendorRate: rates.vendorRate });
        }
    }

    get updatedRates(): OfferRates {
        return this._offerRates;
    }

    @Input()
    set updatedVendorRate(vendorRate: number) {
        if (vendorRate !== undefined) {
            this._vendorRate = vendorRate;
            this.detailsFormGroup.patchValue({ vendorRate });
        }
    }

    get updatedVendorRate(): number {
        return this._vendorRate;
    }

    @Input() offerNotes: OfferNotes[];

    @Input() editMode: boolean;

    @Input()
    set attachments(data: any) {
        if (data) {
            this.attachmentData = data;
        }
    }

    get attachments(): any {
        return this.attachmentData;
    }

    @Input()
    set vendorContacts(lookups: VendorLookups) {
        if (lookups) {
            this.vendorLookups = lookups;
            this.candidates = lookups.candidates;
            const activeVendorContact = this.vendorLookups.contacts.find(
                (vl) => vl.id === this.detailsFormGroup.get('vendorContactId').value
            );
            const candidateId = this.detailsFormGroup.get('candidateId').value;
            const existingCandidate = this.candidates
                ? this.candidates.find((item) => item.userId === candidateId)
                : null;
            this.detailsFormGroup.get('selectedCandidate').patchValue(existingCandidate);
            if (!activeVendorContact) {
                this.detailsFormGroup.patchValue({ vendorContactId: '' });
            }
        }
    }

    get vendorContacts(): VendorLookups {
        return this.vendorLookups;
    }

    getRtoApprovalStatus(): string {
        let status: string;
        switch (this.offerData?.rtoApprovalStatus) {
            case RTOApprovalStatuses.Approved: {
                status = 'Approved';
                break;
            }
            case RTOApprovalStatuses.PendingApproval: {
                status = 'Pending Approval';
                break;
            }
            default: {
                status = 'Not Approved';
                break;
            }
        }
        return status;
    }

    @Output() goBackClicked = new EventEmitter();
    @Output() addNoteEmit = new EventEmitter();
    @Output() enableEdit = new EventEmitter();
    @Output() enableView = new EventEmitter();
    @Output() facilitySelectionChange = new EventEmitter();
    @Output() professionSelectionChange = new EventEmitter();
    @Output() updateRatesEmit = new EventEmitter();
    @Output() updateVendorRateEmit = new EventEmitter();
    @Output() updateShowAPN = new EventEmitter();
    @Output() updateAPNReview = new EventEmitter();
    @Output() updateShowVendor = new EventEmitter();
    @Output() updateContractRequestedOffer = new EventEmitter();
    @Output() updateOfferEmit = new EventEmitter();
    @Output() jobIdSelectionChange = new EventEmitter();
    @Output() attachmentUpload = new EventEmitter();
    @Output() attachmentUpdate = new EventEmitter();
    @Output() attachmentDelete = new EventEmitter();
    @Output() attachmentGetFile = new EventEmitter();
    @Output() rescind = new EventEmitter();
    @Output() facilityRateCheck = new EventEmitter();
    @Output() calculateContractDetails = new EventEmitter();
    @Output() jobIdClick = new EventEmitter();
    @Output() declineOfferClick = new EventEmitter();
    @Output() doVendorSearch = new EventEmitter();
    @Output() doFacilitySearch = new EventEmitter();
    @Output() declineOfferEmit = new EventEmitter();
    @Output() openFacilityLink = new EventEmitter<number>();
    @Output() showToRecruiter = new EventEmitter();
    @Output() offerVerballyAccepted = new EventEmitter<number>();

    id: number;
    detailsFormGroup: UntypedFormGroup;
    lookups$: Observable<DetailLookups>;
    professions: Professions[] = [];
    facilities: Facilities[] = [];
    candidates: Candidates[] = [];
    professionTypes: ProfessionTypes[] = [];
    positionTypes: ProfessionTypes[] = [];
    lookupJobIds: number[] = [];
    filteredJobIdOptions: Observable<number[]>;
    shiftValues = [1, 2, 3, 4, 5, 6];
    hoursValue = [7.5, 8, 9.5, 10, 11.5, 12];
    invalidChars = ['-', '+', 'e'];
    minDate: Date = dayjs().toDate();
    maxDate: Date = null;
    viewAltShift = false;
    isRotatingShift = false;
    enteredNoteText = '';
    daysSelected: any[] = [];
    daysRequested = '';
    lengthValue = null;
    filteredFacilityIdOptions: Observable<Facility[]>;
    filteredCandidateOptions: Observable<Candidates[]>;
    minStartDate = new Date();
    reasonDialogRef: MatDialogRef<OfferReasonDialogComponent>;
    declineDialogRef: MatDialogRef<DialogSharedDeclineOfferComponent>;
    declinePanelRef: MatDialogRef<DeclinePanelComponent>;
    minEndDate: Date;
    urlParent: string;
    userId: number;
    jobProfessionName: string | null;

    isEnableClinicalUnitDescriptionUpgrade = false;
    contractLengthDaysInfoMessage = '';
    contractLengthDaysApprovalRequired = false;
    displayedContractLengthWeeks = 0;
    displayedContractLengthDays = 0;
    endDateTextWithoutWeeks = '';
    isAyaPaidBonusEnabled$ = this._featureManager.isEnabled(FeatureFlag.MarginAyaPaidBonus);
    showRTOAlertOnStartEndDates = false;
    enableRtoAlertMessageForViewEditOfferFlag = toSignal(
        this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            ? this._featureManager.isEnabled(FeatureFlag.EnableRTOAlertMessage)
            : of(false)
    );

    candidateJobProfessionValidator = (control: AbstractControl): ValidationErrors | null => {
        const professionId = control.get('professionId');
        const candidateProfessionId = control.get('candidateProfessionId');

        const result =
            professionId && candidateProfessionId && professionId.value !== candidateProfessionId.value
                ? { candidateJobProfessionDifferent: true }
                : null;
        return result;
    };

    constructor(
        @Inject(APP_CONFIG) _settings: Settings,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _renderer: Renderer2,
        private readonly _formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        public confirmationDialogService: ConfirmDialogService,
        private readonly _identityService: IdentityService,
        private readonly _offersService: SharedOffersService,
        private readonly _offersHelper: OffersHelper,
        private readonly _toasterService: ToasterService,
        private readonly _route: ActivatedRoute,
        private readonly _accountManagersService: AccountManagersService,
        private readonly _featureManager: LDFeatureManager,
        private readonly _directOfferQuoteService: DirectOfferMarginService,
        private readonly _store: Store,
        private readonly unitDescriptionSecureWindow: UnitDescriptionSecureWindowService,
        private readonly _marginCalculatorMarginService: MarginCalculatorMarginService
    ) {
        this.ayaAppsDomain = _settings.ayaAppsDomain;
    }

    ngOnInit() {
        this.userId = this._identityService.current?.contactId;
        this.detailsFormGroup = this._formBuilder.group({
            id: new UntypedFormControl(''),
            contractLength: new UntypedFormControl(''),
            contractLengthDays: new UntypedFormControl(''),
            selectedCandidate: new UntypedFormControl(''),
            facilityValues: new UntypedFormControl(''),
            candidateId: new UntypedFormControl(''),
            candidateEmail: new UntypedFormControl(''),
            facilityId: new UntypedFormControl(''),
            contractTypeId: new UntypedFormControl(''),
            positionType: new UntypedFormControl(''),
            jobId: new UntypedFormControl({ value: '', disabled: true }),
            candidateProfessionId: new UntypedFormControl(''),
            professionId: new UntypedFormControl(''),
            expertiseId: new UntypedFormControl(''),
            startDate: new UntypedFormControl(''),
            endDate: new UntypedFormControl(''),
            shifts: new UntypedFormControl(''),
            shiftTypeId: new UntypedFormControl(''),
            shiftsAlt: new UntypedFormControl(''),
            hours: new UntypedFormControl(''),
            hoursAlt: new UntypedFormControl(''),
            shiftTimeOfDayId: new UntypedFormControl(''),
            shiftAltTimeOfDayId: new UntypedFormControl(''),
            shiftTimeStart: new UntypedFormControl(''),
            shiftTimeEnd: new UntypedFormControl(''),
            shiftAltTimeStart: new UntypedFormControl(''),
            shiftAltTimeEnd: new UntypedFormControl(''),
            shiftM: new UntypedFormControl(''),
            shiftH: new UntypedFormControl(''),
            shiftToM: new UntypedFormControl(''),
            shiftToH: new UntypedFormControl(''),
            shiftAltM: new UntypedFormControl(''),
            shiftAltH: new UntypedFormControl(''),
            shiftAltToM: new UntypedFormControl(''),
            shiftAltToH: new UntypedFormControl(''),
            shiftDetails: new UntypedFormControl(''),
            hasRequestedTimeOff: new UntypedFormControl(''),
            requestedTimeOff: new UntypedFormControl(''),
            requestedTimeOffIsApproved: new UntypedFormControl(''),
            vendorContactId: new UntypedFormControl(''),
            vendorRate: new UntypedFormControl(''),
            facilityRate: new UntypedFormControl(''),
            otherDetails: new UntypedFormControl(''),
            status: new UntypedFormControl({ value: '', disabled: true }),
            isContractRequestStatus: new UntypedFormControl({ value: '', disabled: true }),
            specialRequestNote: new UntypedFormControl(''),
            offerType: new UntypedFormControl(''),
            isRemoteBooking: new UntypedFormControl(''),
            rtoApprovalStatus: new UntypedFormControl(''),
            ayaPaidBonus: new UntypedFormControl('')
        });

        const shiftTypeIdCtrl = this.detailsFormGroup.controls.shiftTypeId;
        const shiftsCtrl = this.detailsFormGroup.controls.shifts;
        const hoursCtrl = this.detailsFormGroup.controls.hours;
        const shiftTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftTimeOfDayId;

        shiftTypeIdCtrl.setValidators(Validators.required);
        shiftsCtrl.setValidators(Validators.required);
        hoursCtrl.setValidators(Validators.required);
        shiftTimeOfDayIdCtrl.setValidators(Validators.required);

        shiftTypeIdCtrl.updateValueAndValidity();
        shiftsCtrl.updateValueAndValidity();
        hoursCtrl.updateValueAndValidity();
        shiftTimeOfDayIdCtrl.updateValueAndValidity();

        if (this.detailsFormGroup) {
            this.detailsFormGroup
                .get('shiftTypeId')
                .valueChanges.pipe(untilDestroyed(this))
                .subscribe((shift) => {
                    this.validateShiftFields(shift);
                });
        }

        this.filteredCandidateOptions = this.detailsFormGroup.get('selectedCandidate').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterCandidates(val);
                } else {
                    return this.candidates.slice();
                }
            })
        );

        this.filteredFacilityIdOptions = this.detailsFormGroup.get('facilityValues').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterFacilities(val);
                } else {
                    if (this.facilityLookups) {
                        this.facilityLookups.professions = [];
                        this.facilityLookups.jobs = [];
                        this.facilityLookups.accountManager = '';
                    }
                    return this.facilities.slice();
                }
            })
        );
        this.showDaysRequested();
        this._route.parent.url.subscribe((url) => (this.urlParent = url[0].path));

        this._featureManager
            .isEnabled(FeatureFlag.EnableClinicalUnitDescriptionUpgrade)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.isEnableClinicalUnitDescriptionUpgrade = flagIsEnabled;
            });

        this.detailsFormGroup.addValidators(this.candidateJobProfessionValidator);

        this._store.dispatch(getSubmittalStatuses());

        this._featureManager
            .isEnabled(CodeBustersFeatureFlags.DeclineOfferCategory)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isDeclineOfferCategoryEnabled = isEnabled;
            });

        this._featureManager
            .isEnabled(FeatureFlag.ConnectVMSRecruiterSpecificOfferNotes)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isRecruiterSpecificNoteFlagEnabled = isEnabled;
            });

        this.offerRescindedSubStauses$ = this._store.select(offerRescindedSubStatuses);

        this._featureManager
            .isEnabled(FeatureFlag.LotusOneUrlReferencesNova)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isLotusOneUrlReference = isEnabled;
            });
    }

    get validateCandidateProfessionJobs(): boolean {
        if (this.editMode) {
            const jobProfessionIdControl = this.detailsFormGroup.controls.professionId;
            const candidateProfessionIdControl = this.detailsFormGroup.controls.candidateProfessionId;

            return (
                jobProfessionIdControl &&
                candidateProfessionIdControl &&
                jobProfessionIdControl.value !== candidateProfessionIdControl.value
            );
        } else {
            return false;
        }
    }

    private validateShiftFields(shift: any) {
        const shiftsAltCtrl = this.detailsFormGroup.controls.shiftsAlt;
        const hoursAltCtrl = this.detailsFormGroup.controls.hoursAlt;
        const shiftAltTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftAltTimeOfDayId;

        this.isRotatingShift = shift === 3;
        if (this.isRotatingShift) {
            // when flag is on, clear alt shifts, hours values for rotating shift
            shiftsAltCtrl.patchValue('');
            hoursAltCtrl.patchValue('');
        }

        this.viewAltShift = shift === 2 ? true : false;

        shiftsAltCtrl.clearValidators();
        hoursAltCtrl.clearValidators();
        shiftAltTimeOfDayIdCtrl.clearValidators();

        if (this.isRotatingShift) {
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        } else if (this.viewAltShift) {
            shiftsAltCtrl.setValidators(Validators.required);
            hoursAltCtrl.setValidators(Validators.required);
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        }

        shiftsAltCtrl.updateValueAndValidity();
        hoursAltCtrl.updateValueAndValidity();
        shiftAltTimeOfDayIdCtrl.updateValueAndValidity();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.startDate?.currentValue) {
            this.detailsFormGroup.get('startDate').patchValue(changes.startDate.currentValue);
        }
        if (changes.endDate?.currentValue) {
            if (this.detailsFormGroup.get('startDate').value) {
                this.detailsFormGroup.get('endDate').patchValue(changes.endDate.currentValue);
                this.validateRTODatesforEditOrViewOffer();
            }
        }
        if (changes.vendorOptions?.currentValue && this.declineDialogRef) {
            this.declineDialogRef.componentInstance.data.vendorOptions = changes.vendorOptions.currentValue;
            this.declineDialogRef.componentInstance.isAgencyLoading = false;
        }
        if (changes.facilityOptions?.currentValue && this.declineDialogRef) {
            this.declineDialogRef.componentInstance.data.facilityOptions = changes.facilityOptions.currentValue;
            this.declineDialogRef.componentInstance.isFacilityLoading = false;
        }
        if (this.isDeclineOfferCategoryEnabled) {
            if (changes.facilityOptions?.currentValue && this.declinePanelRef) {
                this.declinePanelRef.componentInstance.data.facilityOptions = changes.facilityOptions.currentValue;
                this.declinePanelRef.componentInstance.isFacilityLoading = false;
            }
            if (changes.vendorOptions?.currentValue && this.declinePanelRef) {
                this.declinePanelRef.componentInstance.data.vendorOptions = changes.vendorOptions.currentValue;
                this.declinePanelRef.componentInstance.isAgencyLoading = false;
            }
        }
    }

    hasError = (controlName: string, errorName: string) => {
        return this.detailsFormGroup.controls[controlName].hasError(errorName);
    };

    setAgencyOfferFieldsAndValidations(): void {
        this.detailsFormGroup.get('selectedCandidate').setValidators([Validators.required]);
        this.detailsFormGroup.get('facilityValues').setValidators([Validators.required]);
        this.detailsFormGroup.get('vendorContactId').setValidators([Validators.required]);
    }

    setDirectOfferFieldsAndValidattions(): void {
        if (this.detailsFormGroup) {
            this.detailsFormGroup.get('startDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('endDate').setValidators([Validators.required]);
        }
    }

    setContractDateFieldsValidations(): void {
        if (this.detailsFormGroup) {
            this.detailsFormGroup.get('startDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('endDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLength').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLengthDays').setValidators([Validators.required]);
        }
    }

    filterCandidates(val: any) {
        return val?.id
            ? this.candidates.filter((candidates) => candidates.id === val.id)
            : this.candidates.filter((candidates) =>
                  candidates.name ? candidates.name.toLowerCase().startsWith(val.toLowerCase()) : ''
              );
    }

    filterFacilities(val: any) {
        return val?.id
            ? this.facilities.filter((facility) => facility.id === val.id)
            : this.facilities.filter((facility) =>
                  facility.name ? facility.name.toLowerCase().startsWith(val.toLowerCase()) : ''
              );
    }

    filterJobIds(val: any) {
        const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
        return this._offersService.filterJobIds(val, this.facilityLookups, candidateProfessionId);
    }

    displayJobIds(id: any) {
        if (id) {
            return id;
        }
        return null;
    }

    focusOutContract() {
        const val = this.detailsFormGroup.get('contractLength').value;
        const start = this.detailsFormGroup.get('startDate').value;
        if (Number.isInteger(val)) {
            if (start) {
                this.detailsFormGroup.get('endDate').patchValue('');
            }
            this.calculateContract();
        }
    }

    goBack() {
        this.goBackClicked.emit();
    }

    enableEditMode() {
        this.enableEdit.emit();
        this.rateEnabled = false;
    }

    enableViewMode() {
        this.enableView.emit();
        this.rateEnabled = false;
    }

    patchEditValues() {
        const timeStart = dayjs().toDate();
        const timeEnd = dayjs().toDate();
        const timeAltStart = dayjs().toDate();
        const timeAltEnd = dayjs().toDate();

        timeStart.setHours(this._offerDetails.shiftH);
        timeStart.setMinutes(this._offerDetails.shiftM);

        timeEnd.setHours(this._offerDetails.shiftToH);
        timeEnd.setMinutes(this._offerDetails.shiftToM);

        timeAltStart.setHours(this._offerDetails.shiftAltH);
        timeAltStart.setMinutes(this._offerDetails.shiftAltM);

        timeAltEnd.setHours(this._offerDetails.shiftAltToH);
        timeAltEnd.setMinutes(this._offerDetails.shiftAltToM);

        this.detailsFormGroup.patchValue({
            ...this._offerDetails,
            shiftTimeStart: timeStart,
            shiftTimeEnd: timeEnd,
            shiftAltTimeStart: timeAltStart,
            shiftAltTimeEnd: timeAltEnd,
            contractLength: this.displayedContractLengthWeeks,
            contractLengthDays: this.displayedContractLengthDays
        });

        this.daysSelected = this._offerDetails.requestedTimeOff;
        this.showDaysRequested();
        this.calculateContract(null, true);

        this.detailsFormGroup.patchValue({
            professionId: this._offerDetails.professionId
        });
        this.jobProfessionName = this._offerDetails.professionName;
    }

    jobIdSelection(event): void {
        const val = event.option?.value || event.target?.value;
        if (val) {
            this.jobIdSelectionChange.emit(val);
        }
    }

    startDateSelected(event): void {
        const isBefore = dayjs(this.detailsFormGroup.get('endDate').value).isBefore(event.value);
        if (isBefore) {
            this.detailsFormGroup.patchValue({ endDate: null });
        }
        this.minEndDate = new Date(event.value);
        this.calculateContract();
    }

    facilitySelection(event) {
        this.detailsFormGroup.patchValue({ jobId: '' });
        this.detailsFormGroup.patchValue({ facilityId: event.option.value.id });
        this.facilitySelectionChange.emit(event.option.value.id);
    }

    professionSelection(professionId: number, checkRateUpdateFields = true) {
        this.professionSelectionChange.emit(professionId);
        this.detailsFormGroup.patchValue({ expertiseId: null });
        if (checkRateUpdateFields) {
            this.checkRateUpdateFields('Profession');
        }
    }

    candidateSelection(event) {
        const candidate = event.option.value as Candidates;
        if (!candidate) {
            return;
        }

        const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
        if (this._offerDetails && candidateProfessionId !== candidate.professionId) {
            this._offerDetails.professionId = candidate.professionId;
            this.detailsFormGroup.patchValue({
                candidateProfessionId: candidate.professionId,
                jobId: ''
            });
            this.professionSelection(candidate.professionId);
        }

        this.detailsFormGroup.patchValue({
            candidateId: candidate.userId,
            candidateEmail: candidate.email,
            vendorContactId: candidate.vendorContactId
        });
    }

    calculateContract(endChanged = null, firstLoad = false) {
        const obj = {
            endChanged: endChanged,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            jobId: this.detailsFormGroup.get('jobId').value,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            firstLoad: firstLoad
        };
        this.calculateContractDetails.emit(obj);
    }

    calculateContractLength(): number {
        if (this.detailsFormGroup) {
            return Math.round(
                (new Date(this.detailsFormGroup.get('endDate').value).getTime() -
                    new Date(this.detailsFormGroup.get('startDate').value).getTime()) /
                    (7 * 24 * 60 * 60 * 1000)
            );
        } else {
            return 0;
        }
    }

    getDuration(start: Date, end: Date) {
        const startInput = dayjs(start);
        const endInput = dayjs(end);
        const duration = dayjs(endInput).diff(startInput, 'days');
        return `${duration} days`;
    }

    get offerStartDate(): Date | null {
        if (!this.enableRtoAlertMessageForViewEditOfferFlag()) {
            return null;
        }
        return this.offerData?.canEdit === false
            ? dayjs(this.offerData?.startDate).toDate()
            : (this.detailsFormGroup.get('startDate')?.value as Date | null);
    }

    get offerEndDate(): Date | null {
        if (!this.enableRtoAlertMessageForViewEditOfferFlag()) {
            return null;
        }
        return this.offerData?.canEdit === false
            ? dayjs(this.endDateTextWithoutWeeks).toDate()
            : (this.detailsFormGroup.get('endDate')?.value as Date | null);
    }

    get offerTimeOffDates(): Date[] {
        if (!this.enableRtoAlertMessageForViewEditOfferFlag()) {
            return [];
        }
        return this.offerData?.offerTimeOffDates?.map((date) => dayjs(date).toDate()) || [];
    }

    openRescindReasonDialog() {
        this.isOperationProcessing = true;
        this.offerRescindedSubStauses$.pipe(untilDestroyed(this)).subscribe((result: SubmittalSubStatus[]) => {
            const list = result.map((val) => {
                return { id: val.id, name: val.name } as unknown as ListItem;
            });

            this.loadRescindDialog(list);
        });
    }

    loadRescindDialog(reasons: ListItem[]) {
        this.dialog
            .open(OfferReasonDialogComponent, {
                data: { id: this.offerData.id, reasons }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    const offerReasonResult: OfferReasonResult = result;
                    offerReasonResult.submittalSubStatus = result.reasonId;
                    offerReasonResult.reasonId = null;
                    this.rescind.emit(offerReasonResult);
                }
                this.isOperationProcessing = false;
            });
    }

    refreshShiftEndTime() {
        const hours = this.detailsFormGroup.get('hours').value;
        const shiftTimeStart = this.detailsFormGroup.get('shiftTimeStart').value;
        if (hours && shiftTimeStart) {
            const endTime = this._offersHelper.getShiftEndTime(hours, shiftTimeStart);
            this.detailsFormGroup.patchValue({ shiftTimeEnd: endTime });
            this.onTimeEndChanged(endTime);
        }
    }

    refreshShiftEndTimeAlt() {
        const hoursAlt = this.detailsFormGroup.get('hoursAlt').value;
        const shiftAltTimeStart = this.detailsFormGroup.get('shiftAltTimeStart').value;
        if (hoursAlt && shiftAltTimeStart) {
            const endTimeAlt = this._offersHelper.getShiftEndTime(hoursAlt, shiftAltTimeStart);
            this.detailsFormGroup.patchValue({ shiftAltTimeEnd: endTimeAlt });
            this.onAltTimeEndChanged(endTimeAlt);
        }
    }

    onShiftLengthAltChanged() {
        this.refreshShiftEndTimeAlt();
    }

    onTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftH: Number(hour),
            shiftM: Number(min)
        });

        this.refreshShiftEndTime();
    }

    onAltTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltH: Number(hour),
            shiftAltM: Number(min)
        });

        this.refreshShiftEndTimeAlt();
    }

    onTimeEndChanged(value: Date) {
        this.detailsFormGroup.patchValue({
            shiftToH: value.getHours(),
            shiftToM: value.getMinutes()
        });
    }

    onAltTimeEndChanged(value: Date) {
        this.detailsFormGroup.patchValue({
            shiftAltToH: value.getHours(),
            shiftAltToM: value.getMinutes()
        });
    }

    dateClass(): (date: Date) => MatCalendarCellCssClasses {
        const isRtoAlertEnabledForEditOffer = this.enableRtoAlertMessageForViewEditOfferFlag();
        return (date: Date): MatCalendarCellCssClasses => {
            if (isRtoAlertEnabledForEditOffer) {
                return RtoDatesHelpers.getCssClassForDate(
                    date,
                    this.offerStartDate,
                    this.offerEndDate,
                    this.daysSelected
                );
            }
            const matchFound = this.daysSelected.find((value) => dayjs(value).toDate().getTime() === date.getTime());
            return !matchFound ? null : this.constDateSelected;
        };
    }

    dateChanged(event: string): void {
        const foundDateIndex = this.daysSelected.findIndex((date) => dayjs(date).isSame(dayjs(event)));
        const dateNode = this._getDateNode(event);
        let formattedDate = [];

        if (foundDateIndex < 0) {
            this.daysSelected.push(event);
            this.daysSelected = this._sortDates(this.daysSelected);

            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            const isStartOrEndDate =
                this.enableRtoAlertMessageForViewEditOfferFlag() &&
                RtoDatesHelpers.addCssClassForOfferStartEndDates(
                    new Date(event),
                    this.offerStartDate,
                    this.offerEndDate
                );
            this._renderer.addClass(
                dateNode,
                isStartOrEndDate ? this.constDateContractStartEndClass : this.constDateSelected
            );
        } else {
            this.daysSelected.splice(foundDateIndex, 1);
            this.daysSelected = this._sortDates(this.daysSelected);
            formattedDate = [];
            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);

            this._renderer.removeClass(dateNode, this.constDateSelected);
            if (this.enableRtoAlertMessageForViewEditOfferFlag()) {
                this._renderer.removeClass(dateNode, this.constDateContractStartEndClass);
            }
        }

        this.showDaysRequested();
    }

    clearDates() {
        this.daysSelected = [];
        this.calendar.updateTodaysDate();
        this.clearCalendarTrigger.closeMenu();
        this.detailsFormGroup.get('requestedTimeOff').patchValue([]);
        this.showDaysRequested();
        this.validateRTODatesforEditOrViewOffer();
    }

    showDaysRequested(): void {
        if (this.daysSelected.length === 0) {
            this.daysRequested = '';
            this.validateRTODatesforEditOrViewOffer();
            return;
        }

        if (this.daysSelected.length === 1) {
            this.daysRequested = `${this._mapDatesToFormat(this.daysSelected).join('')}`;
            this.validateRTODatesforEditOrViewOffer();
            return;
        }

        let dateRangeStr = '';
        let nonDateRangeStr = '';
        const dateRangeSet = new Set<Date[]>();
        const nonDateRangeSet = new Set<Date[]>();

        let currentConsecutive: Date[] = [];

        this.daysSelected.forEach((value) => {
            if (currentConsecutive.length === 0) {
                currentConsecutive.push(value);
                nonDateRangeSet.add(currentConsecutive);
                return;
            }

            const lastConsecutivePair = currentConsecutive[currentConsecutive.length - 1];

            if (dayjs(value).diff(lastConsecutivePair, 'days') === 1) {
                currentConsecutive.push(value);
            } else {
                currentConsecutive = [value];
            }

            if (currentConsecutive.length > 1) {
                dateRangeSet.add(currentConsecutive);

                for (const element of nonDateRangeSet) {
                    // Filter out dates from the non consecutive date range
                    if (dateRangeSet.has(element)) {
                        nonDateRangeSet.delete(element);
                    }
                }
            } else {
                nonDateRangeSet.add(currentConsecutive);
            }
        });

        dateRangeStr = this._buildDateRangeStr(dateRangeSet, false);
        nonDateRangeStr = this._buildDateRangeStr(nonDateRangeSet, true);

        if (nonDateRangeStr !== '' && dateRangeStr === '') {
            this.daysRequested = `${nonDateRangeStr}`;
        } else if (dateRangeStr !== '' && nonDateRangeStr === '') {
            this.daysRequested = `${dateRangeStr}`;
        } else {
            this.daysRequested = `${nonDateRangeStr}, ${dateRangeStr}`;
        }
        this.validateRTODatesforEditOrViewOffer();
    }

    validateRTODatesforEditOrViewOffer() {
        if (!this.enableRtoAlertMessageForViewEditOfferFlag()) {
            return;
        }
        const vacationList = this.offerData?.canEdit === false ? this.offerTimeOffDates : this.daysSelected;
        this.showRTOAlertOnStartEndDates = RtoDatesHelpers.shouldDisplayRTOAlertOnStartOrEndDate(
            this.offerStartDate,
            this.offerEndDate,
            vacationList
        );
    }

    addNote() {
        if (this.userType === OffersUserType.Recruiter) {
            this.addNoteEmit.emit({ note: this.enteredNoteText, title: 'Note' });
        } else {
            this.addNoteEmit.emit(this.enteredNoteText);
        }

        this.enteredNoteText = '';
    }

    addNoteWithCategory(offerNoteCategoryId: CandidateOfferNoteCategory) {
        this.addNoteEmit.emit({
            note: this.enteredNoteText,
            offerNoteCategoryId
        });

        this.enteredNoteText = '';
    }

    updateStatusShowAPN() {
        this.updateShowAPN.emit();
    }

    updateStatusAPNReview() {
        this.updateAPNReview.emit();
    }

    updateStatusShowVendor() {
        if (this.detailsFormGroup.value.facilityRate > 0) {
            this.updateShowVendor.emit();
        } else {
            this.checkFacilityRate('Vendor Review');
        }
    }

    updateStatusShowRecruiter() {
        this.showToRecruiter.emit();
    }

    openContractRequestDialog(updateStatusOnly: boolean) {
        const expertiseId = this.detailsFormGroup.value.expertiseId;
        const candidateProfessionId = this.detailsFormGroup.value.candidateProfessionId;
        if (candidateProfessionId && !expertiseId) {
            this._toasterService.toast('Cannot update the offer. Expertise is empty.', 20000);
            return;
        }

        if (this.detailsFormGroup.value.facilityRate > 0) {
            this.confirmationDialogService
                .showDialog({
                    title: 'Contract Request',
                    message:
                        'You are submitting a contract request without showing to vendor. Are you sure you would like to proceed?',
                    action: 'Confirm',
                    cancelText: 'Cancel'
                })
                .subscribe((isContractRequestConfirmed) => {
                    if (isContractRequestConfirmed) {
                        const dialogConfig = {
                            maxWidth: '600px',
                            disableClose: true,
                            panelClass: ['nova__dialog', 'complete-offer-dialog'],
                            data: {
                                shiftDetails: this.detailsFormGroup.value.shiftDetails,
                                otherDetails: this.detailsFormGroup.value.otherDetails
                            }
                        };
                        this.dialog
                            .open(DialogContractRequestedOfferComponent, dialogConfig)
                            .afterClosed()
                            .subscribe((result) => {
                                if (result?.send) {
                                    const specialRequestNote = result.requestStr;
                                    if (updateStatusOnly) {
                                        this.updateContractRequestedOffer.emit(specialRequestNote);
                                    } else {
                                        this.updateOfferAndRequestContract(specialRequestNote);
                                    }
                                }
                            });
                    }
                });
        } else {
            this.checkFacilityRate('Contract Request');
        }
    }

    openMargin(marginId: number, recalculateInsurance = false) {
        if (marginId) {
            if (this._isLdFeatureFlagEnabled(this.featureFlag.ImportOfferMarginInsuranceRecalculate)) {
                window.open(
                    `${window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}?recalculateInsurance=${recalculateInsurance}`,
                    '_blank',
                    'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
                );
            } else {
                window.open(
                    `${window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                    '_blank',
                    'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
                );
            }
        }
    }

    openMarginCalculator(marginId: number) {
        if (this.offerData?.isSubcontractor) {
            this._window.open(
                `${this._window.location.origin}/#/subcontractor-margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                '_blank',
                'width=1366,height=970,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
            );
        } else {
            this._window.open(
                `${this._window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                '_blank',
                'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
            );
        }
    }

    updateOfferAndRequestContract(specialRequest: string) {
        const data = this.detailsFormGroup.value;
        data.updatedStatusId = OfferStatus.ContractRequested;
        data.specialRequestNote = specialRequest;
        this.updateOffer();
    }

    updateOffer(vendor = false) {
        const data = this.detailsFormGroup.value;
        if (vendor) {
            data.updatedStatusId = OfferStatus.VendorReview;
        } else if (this.offerData.canReactivateOffer) {
            data.updatedStatusId = OfferStatus.Pending;
        } else {
            if (this.detailsFormGroup.get('isContractRequestStatus').value && isEmpty(data.updatedStatusId)) {
                data.updatedStatusId = OfferStatus.ContractChange;
            }
        }

        if (data.shiftTypeId) {
            data.hasShifts = true;
        }

        if (!data.hasRequestedTimeOff) {
            data.requestedTimeOff = null;
        }
        this.updateOfferEmit.emit(data);
        this.addUpdatedFieldsNotes();
    }

    addUpdatedFieldsNotes() {
        let notes = '';
        const dateFormat = 'MM/dd/yyyy';
        const locale = 'en';
        let contractLength = false;

        const data = this.detailsFormGroup.value;

        if (data.contractTypeId !== this.offerDetails?.contractTypeId) {
            notes = [notes, 'Contract Type'].filter(Boolean).join(', ');
        }
        if (data.jobId !== this.offerDetails?.jobId) {
            notes = [notes, 'Job Id'].filter(Boolean).join(', ');
        }
        if (data.expertiseId !== this.offerDetails?.expertiseId) {
            notes = [notes, 'Specialty'].filter(Boolean).join(', ');
        }
        if (
            formatDate(data.startDate ? data.startDate : null, dateFormat, locale) !==
            formatDate(this.offerDetails?.startDate ? this.offerDetails?.startDate : null, dateFormat, locale)
        ) {
            notes = [notes, 'Start Date'].filter(Boolean).join(', ');
            contractLength = true;
        }
        if (
            formatDate(data.endDate ? data.endDate : null, dateFormat, locale) !==
            formatDate(this.offerDetails?.endDate ? this.offerDetails?.endDate : null, dateFormat, locale)
        ) {
            notes = [notes, 'End Date'].filter(Boolean).join(', ');
            contractLength = true;
        }
        if (contractLength === true) {
            notes = [notes, 'Contract Length'].filter(Boolean).join(', ');
        }

        if (data.shiftTypeId !== this.offerDetails?.shiftTypeId) {
            notes = [notes, 'Shift Type'].filter(Boolean).join(', ');
        }
        if (data.shifts !== this.offerDetails?.shifts || data.shiftsAlt !== this.offerDetails?.shiftsAlt) {
            notes = [notes, '# of Shifts'].filter(Boolean).join(', ');
        }
        if (data.hours !== this.offerDetails?.hours || data.hoursAlt !== this.offerDetails?.hoursAlt) {
            notes = [notes, 'Shift Length'].filter(Boolean).join(', ');
        }
        if (
            data.shiftTimeOfDayId !== this.offerDetails?.shiftTimeOfDayId ||
            data.shiftAltTimeOfDayId !== this.offerDetails?.shiftAltTimeOfDayId
        ) {
            notes = [notes, 'Shift Time of Day'].filter(Boolean).join(', ');
        }
        if (
            data.shiftH !== this.offerDetails?.shiftH ||
            data.shiftM !== this.offerDetails?.shiftM ||
            data.shiftAltH !== this.offerDetails?.shiftAltH ||
            data.shiftAltM !== this.offerDetails?.shiftAltM
        ) {
            notes = [notes, 'Shift Time'].filter(Boolean).join(', ');
        }
        if (data.shiftDetails !== this.offerDetails?.shiftDetails) {
            notes = [notes, 'Shift Details'].filter(Boolean).join(', ');
        }
        if (data.hasRequestedTimeOff !== this.offerDetails?.hasRequestedTimeOff) {
            notes = [notes, 'RTO'].filter(Boolean).join(', ');
        }
        if (
            data.requestedTimeOff?.length !== this.offerDetails?.requestedTimeOff?.length ||
            data.requestedTimeOff?.filter((x) => !this.offerDetails?.requestedTimeOff?.includes(x)).length > 0
        ) {
            notes = [notes, 'RTO Dates'].filter(Boolean).join(', ');
        }
        if (data.requestedTimeOffIsApproved !== this.offerDetails?.requestedTimeOffIsApproved) {
            notes = [notes, 'RTO Approval'].filter(Boolean).join(', ');
        }
        if (data.rtoApprovalStatus !== this.offerDetails?.rtoApprovalStatus) {
            notes = [notes, 'RTO Approval'].filter(Boolean).join(', ');
        }
        if (data.facilityRate !== this.offerDetails?.facilityRate) {
            notes = [notes, 'Facility Rate'].filter(Boolean).join(', ');
        }
        if (data.otherDetails !== this.offerDetails?.otherDetails) {
            notes = [notes, 'Other Details'].filter(Boolean).join(', ');
        }

        if (notes.length > 0) {
            this.addNoteEmit.emit(notes);
        }
    }

    attachmentToUpload(file) {
        this.attachmentUpload.emit(file);
    }

    attachmentToUpdate(file) {
        this.attachmentUpdate.emit(file);
    }

    attachmentToDelete(file) {
        this.attachmentDelete.emit(file);
    }

    attachmentToGet(file) {
        this.attachmentGetFile.emit(file);
    }

    checkRateUpdateFields(changeField: string): void {
        if (
            !!this.detailsFormGroup.get('facilityId').value &&
            !!this.detailsFormGroup.get('candidateProfessionId').value &&
            !!this.detailsFormGroup.get('expertiseId').value &&
            !!this.detailsFormGroup.get('hours').value
        ) {
            const ratesRequest = {
                facilityId: this.detailsFormGroup.get('facilityId').value,
                professionId: this.detailsFormGroup.get('candidateProfessionId').value,
                expertiseId: this.detailsFormGroup.get('expertiseId').value,
                hours: this.detailsFormGroup.get('hours').value,
                shiftTimeOfDayId: this.detailsFormGroup.get('shiftTimeOfDayId').value,
                changeField: changeField
            };
            this.updateRatesEmit.emit(ratesRequest);
        }
        if (changeField === 'Shift Length') {
            this.refreshShiftEndTime();
        }
    }

    displayFieldName(option: any) {
        if (option?.name) {
            return option.name;
        }
        return '';
    }

    updateVendorRate(): void {
        const vendorRateRequest = {
            facilityId: this.detailsFormGroup.get('facilityId').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value
        };
        this.updateVendorRateEmit.emit(vendorRateRequest);
    }

    openFacility(facilityId: number) {
        const facilityIdVal = facilityId ? facilityId : this.detailsFormGroup.get('facilityId').value;
        this.openFacilityLink.emit(facilityIdVal);
    }

    rateEdit() {
        this.rateEnabled = !this.rateEnabled;
    }

    validateInput(event) {
        if (this.invalidChars.includes(event.key)) {
            event.preventDefault();
        }
    }

    checkFacilityRate(text: string) {
        this.facilityRateCheck.emit(text);
    }

    togglePanel() {
        this.isPanelOpen = !this.isPanelOpen;
    }

    declineOffer() {
        if (this.isDeclineOfferCategoryEnabled) {
            this.declinePanelRef = this.dialog.open(DeclinePanelComponent, {
                position: { right: '0px', top: '0px' },
                disableClose: true,
                data: {
                    title: 'Decline Offer',
                    declineReasons: this.declineReasons,
                    vendorOptions: this.vendorOptions,
                    facilityOptions: this.facilityOptions
                }
            });

            this.declinePanelRef.componentInstance.doVendorSearch
                .pipe(untilDestroyed(this))
                .subscribe((vendorInput) => {
                    this.doVendorSearch.emit(vendorInput);
                });
            this.declinePanelRef.componentInstance.doFacilitySearch
                .pipe(untilDestroyed(this))
                .subscribe((facilityInput) => {
                    this.doFacilitySearch.emit(facilityInput);
                });

            this.declinePanelRef.afterClosed().subscribe((result) => {
                if (result) {
                    this.declineOfferEmit.emit(result);
                }
            });
        } else {
            this.declineDialogRef = this.dialog.open(DialogSharedDeclineOfferComponent, {
                data: {
                    title: 'Decline Offer',
                    declineReasons: this.declineReasons,
                    vendorOptions: this.vendorOptions,
                    facilityOptions: this.facilityOptions
                },
                disableClose: true,
                panelClass: ['nova__dialog', 'nova__dialog--md'],
                minWidth: 800,
                minHeight: 450
            });

            this.declineDialogRef.componentInstance.doVendorSearch
                .pipe(untilDestroyed(this))
                .subscribe((vendorInput) => {
                    this.doVendorSearch.emit(vendorInput);
                });
            this.declineDialogRef.componentInstance.doFacilitySearch
                .pipe(untilDestroyed(this))
                .subscribe((facilityInput) => {
                    this.doFacilitySearch.emit(facilityInput);
                });
            this.declineDialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    this.declineOfferEmit.emit(result);
                }
            });
        }
    }

    requestChanges() {
        const dialogConfig = {
            disableClose: true,
            panelClass: ['nova__dialog', 'request-changes-dialog'],
            data: {}
        };
        this.dialog
            .open(DialogRequestedChangesComponent, dialogConfig)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    // TBD emit changes
                }
            });
    }

    isWFDEmploymentType(): boolean {
        return this.offerData?.jobEmploymentTypeCode === EmploymentTypeCode.WorkforceDisruption;
    }

    showSampleMarginButton(): boolean {
        return this.isWFDEmploymentType() && this._showMarginButtons() && isEmpty(this.marginId);
    }

    showCreateAndImportMarginButtons(): boolean {
        return this._showMarginButtons() && !this.marginId && !this.isWFDEmploymentType();
    }

    showImportMarginButton(): boolean {
        return this._showMarginButtons() && !isEmpty(this.marginId) && !this.isWFDEmploymentType();
    }

    private _showMarginButtons(): boolean {
        return (
            this.userType === OffersUserType.Recruiter &&
            this._identityService.hasPermission('Recruiting', 'Deals') &&
            this.offerData?.jobId > 0 &&
            this.offerData?.facilityId > 0 &&
            this.offerData?.candidateId > 0 &&
            this.offerData?.offerType === OfferType.Direct &&
            (this.offerData?.statusId === OfferStatus.RecruiterReview ||
                this.offerData?.statusId === OfferStatus.ContractRequested)
        );
    }

    showCreateMarginDialog(): void {
        this.dialog
            .open(DirectOfferCreateMarginDialogComponent, {
                panelClass: 'direct-offer-margin-dialog',
                data: {
                    selectedJob: {
                        id: this.offerData?.jobId,
                        facilityId: this.offerData?.facilityId,
                        facilityName: this.offerData?.facilityName,
                        shiftsText: this.offerData?.shiftsText,
                        jobTitle: this.offerData?.jobTitle
                    },
                    recruiterId: this._identityService.current.contactId,
                    candidateId: this.offerData?.candidateId,
                    candidateName: this.offerData?.candidateName,
                    offerId: this.offerId
                }
            })
            .afterClosed()
            .subscribe((marginId) => {
                if (marginId) {
                    this._marginCreated(marginId, false);
                }
            });
    }

    showImportMarginDialog(): void {
        this.dialog
            .open(DirectOfferImportMarginDialogComponent, {
                panelClass: 'direct-offer-import-dialog',
                data: {
                    facilityId: this.offerData?.facilityId,
                    facilityName: this.offerData?.facilityName,
                    offerId: this.offerId
                }
            })
            .afterClosed()
            .subscribe((marginId) => {
                if (marginId) {
                    this._marginCreated(marginId, true);
                }
            });
    }

    openMarginDifferences() {
        this.dialog.open(OfferMarginDifferencesDialogComponent, {
            panelClass: 'nova__dialog',
            data: {
                userType: this.userType,
                offerId: this.offerId,
                marginId: this.marginId,
                differences: this.offerMarginDifferences,
                candidateName: this.offer.candidateName
            }
        });
    }

    private _marginCreated(marginId: number, recalculateInsurance: boolean) {
        this.openMargin(marginId, recalculateInsurance);
        this.enableView.emit();
        this.addNoteEmit.emit({
            note: `Added Offer Margin [${marginId}]`,
            title: 'Added Offer Margin',
            excludeNotification: true
        });
    }

    goToJobId(id: number) {
        this.jobIdClick.emit(id);
    }

    goToContractRequest(contractRequestId: number, marginId: number) {
        this._window.open(`/#/recruiting/contract-request/${contractRequestId}?marginId=${marginId}`, '_blank');
    }

    openUnitFormUrl($event: MouseEvent, unitFormId: number) {
        $event.preventDefault();
        const url = this._accountManagersService.getUnitFormUrl(
            this.userId,
            unitFormId.toString(),
            this.isLotusOneUrlReference
        );

        this.unitDescriptionSecureWindow.open(url, '_blank', true);
    }

    private _buildDateRangeStr(values: Set<Date[]>, nonConsecutive: boolean): string {
        let count = 0;
        let str = '';

        for (const element of values) {
            const array = nonConsecutive ? [] : this._mapDatesToFormat(Array.of(element)[0]);

            if (count === values.size - 1) {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}`
                    : `${array[0]}-${array[array.length - 1]}`;
            } else {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format('MM/DD/YYYY')}, `
                    : `${array[0]}-${array[array.length - 1]}, `;
            }

            count++;
        }

        return str;
    }

    showCreateContractRequestButton() {
        return (
            !this.editMode &&
            this.marginId &&
            this.showImportMarginButton() &&
            this.offerData?.offerType === this.offerTypeEnum.Direct
        );
    }

    private _getDateNode(date: string): unknown {
        return this._renderer.selectRootElement(`td button[aria-label="${dayjs(date).format('MMMM D, YYYY')}"]`, true);
    }

    private _mapDatesToFormat(dates: Date[], format?: string): string[] {
        return dates.map((date) => dayjs(date).format(format && format !== '' ? format : 'M/D/YY'));
    }

    private _sortDates(dates: Date[]): Date[] {
        return dates.sort((first, second) => dayjs.utc(first).diff(second));
    }

    revertStatusId(originalStatusId: number) {
        this.detailsFormGroup.patchValue({ statusId: originalStatusId });
    }

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }

    initContractLengthDaysAndWeeks(offer: Offer) {
        this.endDateTextWithoutWeeks = offer.endDateText?.split('(')[0];
        const obj = {
            changeType: 'initialLoad',
            startDate: offer.startDate,
            endDate: this.endDateTextWithoutWeeks,
            numOfShifts: offer.shiftsText.split('x')[0],
            facilityRate: offer.facilityRate,
            facilityId: offer.facilityId,
            candidateId: offer.candidateId
        };
        this.validateRTODatesforEditOrViewOffer();
        this.calculateContractDetails.emit(obj);
    }

    calculateContractDates(changeType: string): void {
        const obj = {
            changeType: changeType,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            contractLengthDays: this.detailsFormGroup.get('contractLengthDays').value,
            numOfShifts: this.detailsFormGroup.get('shifts').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value,
            ayaPaidBonus: this.detailsFormGroup.get('ayaPaidBonus').value
        };
        this.calculateContractDetails.emit(obj);
        if (this.enableRtoAlertMessageForViewEditOfferFlag()) {
            this.showDaysRequested();
        }
    }

    createOfferMargin() {
        this.showCreateMarginDialog();
    }

    verballyAcceptOffer(): void {
        this.offerVerballyAccepted.emit(this.offerData.id);
    }

    disableSampleMargin(): boolean {
        return this.offerData?.sampleMarginId <= 0;
    }

    sampleMarginTooltip(): string {
        const toolTip =
            'Unable to create Margin. Please contact the Account Manager to add a sample margin on the job before proceeding.';

        if (this.offerData?.sampleMarginId <= 0) {
            return toolTip;
        }
        return '';
    }

    duplicateSampleMargin(): void {
        this.isLoading = true;
        this._marginCalculatorMarginService
            .duplicateSampleMargin(this.offerData.sampleMarginId, this.offerData.candidateId, this.offerId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (marginId) => {
                    this.isLoading = false;
                    this.marginId = marginId;
                    this._marginCreated(marginId, false);
                },
                error: (errorMessage) => {
                    this.isLoading = false;
                    this._toasterService.toast(errorMessage, 7000);
                }
            });
    }

    get shouldDisplayNoteCategoryButtons() {
        return (
            this.isRecruiterSpecificNoteFlagEnabled &&
            this.userType === OffersUserType.AccountManager &&
            this.offer.offerType === OfferType.Direct
        );
    }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null): boolean {
        return control && control.invalid;
    }
}
